import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, MailWarning } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function DashboardShell({ children, title, eyebrow }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="dashboard-shell">
      {/* Top bar */}
      <header className="bg-white border-b border-[#0A0A0A]/10 sticky top-0 z-40">
        <div className="px-6 md:px-10 lg:px-16 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="dashboard-brand-link"
            className="flex items-center font-display font-black tracking-tighter text-[#0A0A0A]"
          >
            <span className="inline-block w-2.5 h-2.5 bg-[#1E4D55]" />
            <span className="text-xl ml-2">raanah<span className="text-[#1E4D55]">.</span></span>
          </Link>

          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold text-[#0A0A0A] leading-tight" data-testid="dashboard-user-name">
                {user?.name}
              </p>
              <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#475569]" data-testid="dashboard-user-role">
                {user?.role}
              </p>
            </div>
            <div
              aria-hidden="true"
              className="w-10 h-10 bg-[#1E4D55] text-white flex items-center justify-center font-display font-bold text-sm"
            >
              {user?.name?.slice(0, 2).toUpperCase() || "??"}
            </div>
            <button
              onClick={handleLogout}
              data-testid="dashboard-logout-button"
              className="inline-flex items-center gap-2 border border-[#0A0A0A]/15 hover:border-[#1E4D55] hover:text-[#1E4D55] transition-colors px-3 py-2 text-sm font-semibold"
            >
              <LogOut size={14} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-7xl mx-auto">
        {/* Soft email-verification nudge (only for newly-registered users — legacy null = skip) */}
        {user?.email_verified === false && (
          <div
            data-testid="email-verification-banner"
            className="mb-8 bg-[#F5A623]/10 border border-[#F5A623]/40 p-4 flex items-start gap-3"
          >
            <MailWarning size={18} className="text-[#A66800] shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#0A0A0A]">
                Please verify your email to unlock all features.
              </p>
              <p className="mt-1 text-xs text-[#475569]">
                We sent a 6-digit code to <strong>{user.email}</strong>. Tap below to enter it.
              </p>
            </div>
            <button
              onClick={() => navigate("/verify-email")}
              data-testid="email-verification-banner-cta"
              className="shrink-0 bg-[#1E4D55] hover:bg-[#163C42] text-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em]"
            >
              Verify now
            </button>
          </div>
        )}

        {eyebrow && (
          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">
            {eyebrow}
          </p>
        )}
        <h1 className="font-display mt-3 text-4xl sm:text-5xl tracking-tighter font-black text-[#0A0A0A] leading-[1]">
          {title}
        </h1>
        <div className="mt-10">{children}</div>
      </main>
    </div>
  );
}
